import { useBlocker, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleRight, faLock, faLockOpen, faXmark } from "@fortawesome/pro-solid-svg-icons";
import { faPen  } from "@fortawesome/pro-regular-svg-icons";

import useProjectContext from "@/services/context/project/use-project-context.ts";
import usePreventNavigationContext from "@/services/context/prohibit-nagivation/use-prevent-navigation-context";
import ProjectContextProvider from "@/services/context/project/project-context-provider.tsx";

import ProjectLockStatus from "@/models/enums/project-lock-status.ts";

import WebsiteLayout from "@/layouts/website";
import TakeOffPage from "@/pages/takeoff/index.tsx";
import ProjectSelector from "@/components/project-selector";
import Button from "@/components/buttons/button";
import { Case, Switch } from "@/components/switch";
import SimpleLoadingIndicator from "@/components/simple-loading-indicator";
import BaseDialog from "@/components/dialogs/base-dialog/index.tsx";
import Tooltip from "@/components/tooltip";
import PreventNavigationProvider from "@/services/context/prohibit-nagivation/prevent-navigation-provider.tsx";
import LoadingButton from "@/components/buttons/loading-button";
import FlowMode from "@/models/enums/flow-mode.ts";

const TakeOffElement: React.FC = () => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const { project, projectLockStatus, refetchLockStatus, lockProject, unlockProject } = useProjectContext();

    const navigate = useNavigate();

    const { navigationPrevented, hasSaveCallback, setIsNavigationPrevented, callSaveCallback } = usePreventNavigationContext();
    const [isSaving, setIsSaving] = useState(false);

    const blocker = useBlocker(
        useCallback(
            () => navigationPrevented,
            [navigationPrevented]
        )
    );

    const onBeforeUnload = useCallback((event: BeforeUnloadEvent) => {
          event.preventDefault();
    }, []);

    useEffect(() => {
        if(navigationPrevented) {
            window.addEventListener('beforeunload', onBeforeUnload);
        } else {
            window.removeEventListener('beforeunload', onBeforeUnload);
        }
    }, [navigationPrevented, onBeforeUnload]);

    /* GESTION LOCK DU PROJET */
    
    const [isLockDialogOpen, setIsLockDialogOpen] = useState(false);
    const [isModifyHovered, setIsModifyHovered] = useState(false);
    const lockSetRef = useRef(false);

    const onAddLockClick = useCallback(async () => {
        await lockProject();
    }, [lockProject]);

    const onRemoveLockClick = useCallback(async () => {
        setIsLockDialogOpen(false);
        await unlockProject();
    }, [unlockProject]);

    useEffect(() => {
        if (lockSetRef.current) return;
        refetchLockStatus().then(() => {
            lockSetRef.current = true
        });
    }, [refetchLockStatus]);

    return (
        <WebsiteLayout menuRightItem={
            <div className='ml-6.5 flex flex-row gap-x-2 items-center'>
                <Tooltip id='get-lock-tooltip' place='bottom-end'
                         key={`get-lock-tooltip-${projectLockStatus}`}>
                    Le projet n'est pas verrouillé par un tiers. <br />
                    Prenez la main pour modifier.
                </Tooltip>

                <Tooltip id='project-locked-tooltip' place='bottom-end'
                         key={`project-locked-tooltip-${projectLockStatus}`}>
                    Le projet est verrouillé par <b>{project.projectLock?.userLockEmail}</b>. <br />
                    Les modifications ne peuvent pas avoir lieu.
                </Tooltip>

                <Tooltip id='project-locked-by-current-user-tooltip' place='bottom-end'
                         key={`project-locked-by-current-user-tooltip-${projectLockStatus}`}>
                    Vous avez la main sur ce projet.
                </Tooltip>

                <Tooltip id='project-close-tooltip' place='bottom'>
                    Fermer le projet
                </Tooltip>

                <Switch value={projectLockStatus}>
                    <Case value={ProjectLockStatus.LOCK}>
                        <FontAwesomeIcon icon={faLock} className='bg-red-600 rounded-md text-xs px-3 py-1.5 text-white' data-tooltip-id='project-locked-tooltip' />
                    </Case>

                    <Case value={ProjectLockStatus.LOCK_OWNED}>
                        <Button variant='ghost' onClick={() => setIsLockDialogOpen(true)} data-tooltip-id='project-locked-by-current-user-tooltip'>
                            <FontAwesomeIcon icon={faPen} className='text-blue-500' />
                        </Button>
                    </Case>

                    <Case value={ProjectLockStatus.UNLOCK}>
                        <Button variant='success' onClick={onAddLockClick} data-tooltip-id='get-lock-tooltip'>
                            <FontAwesomeIcon icon={faLockOpen} />
                        </Button>
                    </Case>

                    <Case value={ProjectLockStatus.UNDEFINED}>
                        <SimpleLoadingIndicator text='Récupération status' />
                    </Case>
                </Switch>

                <Button variant='ghost' data-tooltip-id='project-close-tooltip' className='cursor-pointer'
                        onClick={() => navigate('/projects')}>
                    <FontAwesomeIcon icon={faXmark} className="text-red-600 text-xl" />
                </Button>

                <Button variant='project' size='xs' onClick={() => setIsDialogOpen(true)}>
                    {project.name}
                </Button>

                <span className='italic text-gray-500 text-sm'>
                    <Switch value={project.flowMode}>
                        <Case value={FlowMode._3D2D}>
                            BIM
                        </Case>
                        <Case value={FlowMode._2D3D}>
                            PDF simple
                        </Case>
                        <Case value={FlowMode._2D3DAdvanced}>
                            PDF superposé
                        </Case>
                    </Switch>
                </span>
            </div>
        }>
            <TakeOffPage />

            <ProjectSelector dialogOpen={isDialogOpen} setDialogOpen={setIsDialogOpen} showCloseButton={true} />
            
            <BaseDialog title='Edition' open={isLockDialogOpen} onClose={() => setIsLockDialogOpen(false)}>
                <div className='flex flex-row gap-x-4 items-center'>
                    <div className='flex flex-col gap-y-2 flex-1'>
                        <span>Vous avez verrouillé le projet.</span>
                        <span>Voulez vous le libérer ?</span>
                    </div>

                    <FontAwesomeIcon icon={faCircleRight} className='text-xl'/>

                    <Button variant={isModifyHovered ? 'success' : 'danger'}
                            className='transition-all duration-200 ease-in-out'
                            onMouseEnter={() => setIsModifyHovered(true)}
                            onMouseLeave={() => setIsModifyHovered(false)}
                            onClick={onRemoveLockClick}>
                        
                        <span className="relative inline-flex size-8 text-xl items-center justify-center">
                            <FontAwesomeIcon
                                icon={faLockOpen}
                                className={`absolute transition-all duration-200 ease-in-out ${
                                    isModifyHovered ? "opacity-100 scale-100 rotate-0" : "opacity-0 scale-90 -rotate-12"}`
                            }/>
                            
                            <FontAwesomeIcon
                                icon={faLock}
                                className={`absolute transition-all duration-200 ease-in-out ${
                                    isModifyHovered ? "opacity-0 scale-90 rotate-12" : "opacity-100 scale-100 rotate-0"}`
                            }/>
                        </span>
                    </Button>
                </div>
            </BaseDialog>

            <BaseDialog open={blocker.state === 'blocked'} onClose={() => blocker.reset?.()}>
                <div>
                    <Button variant='ghost' className='absolute top-1.75 right-1' size='xs'
                            onClick={() => {
                                blocker.reset?.();
                            }} disabled={isSaving}>
                        <FontAwesomeIcon icon={faXmark} className='text-red-500 font-base'/>
                    </Button>
                    
                    <div className='flex flex-col gap-y-4 flex-1 p-1.5'>
                        <p className='text-2xl text-treegram-blue font-semibold'>Enregistrer vos modifications ?</p>
                        <p className='text-gray-800'>Des modifications non enregistrées sont en cours.</p>
                        <div className='flex flex-row gap-x-2 ml-auto mt-4'>
                            <Button onClick={() => {
                                blocker.reset?.();
                            }} variant='simple' disabled={isSaving}>Annuler</Button>
                            <Button onClick={() => {
                                setIsNavigationPrevented(false);
                                blocker.proceed?.();
                            }} variant='simple' disabled={isSaving}>Ne pas enregistrer</Button>
                            
                            {hasSaveCallback && (
                                <LoadingButton
                                    isLoading={isSaving} body='Enregistrer' loadingBody='Enregistrement...'
                                    onClick={async () => {
                                        setIsSaving(true);
                                        const savedSuccessfully = await callSaveCallback();
                                        if (savedSuccessfully) {
                                            setIsNavigationPrevented(false);
                                            blocker.proceed?.();
                                        } else {
                                            blocker.reset?.();
                                        }
                                        setIsSaving(false);
                                    }}/>
                            )}
                        </div>
                    </div>
                </div>
            </BaseDialog>

        </WebsiteLayout>
    )
}

const TakeOffRoute = () => {
    const { id } = useParams<{ id: string }>();

    if (id === undefined) return (
        <div>Renseignez un id</div>
    )

    return (
        <PreventNavigationProvider>
            <ProjectContextProvider projectId={id}>
                <TakeOffElement />
            </ProjectContextProvider>
        </PreventNavigationProvider>
    );
};

export default TakeOffRoute;